import React, { createContext, useContext, useMemo, useState } from 'react';

export type ExpenseCategory =
  | 'Ops'
  | 'Marketing'
  | 'Staff'
  | 'Inventory'
  | 'Other';

export type Expense = {
  id: string;
  description: string;
  category: ExpenseCategory;
  amount: number;
  date: string; // ISO string
  paymentMethod?: string;
};

interface ExpenseContextValue {
  expenses: Expense[];
  addExpense: (expense: Omit<Expense, 'id'>) => void;
  deleteExpense: (id: string) => void;
  total: number;
}

const ExpenseContext = createContext<ExpenseContextValue | undefined>(
  undefined,
);

const seedExpenses: Expense[] = [
  {
    id: 'seed-1',
    description: 'Meta ads - weekend promo',
    category: 'Marketing',
    amount: 320,
    date: new Date().toISOString(),
    paymentMethod: 'Card',
  },
  {
    id: 'seed-2',
    description: 'Fresh produce restock',
    category: 'Inventory',
    amount: 780,
    date: new Date().toISOString(),
    paymentMethod: 'ACH',
  },
];

export const ExpenseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [expenses, setExpenses] = useState<Expense[]>(seedExpenses);

  const addExpense = (expense: Omit<Expense, 'id'>) => {
    setExpenses(prev => [...prev, { ...expense, id: `exp-${Date.now()}` }]);
  };

  const deleteExpense = (id: string) => {
    setExpenses(prev => prev.filter(e => e.id !== id));
  };

  const total = useMemo(
    () => expenses.reduce((sum, exp) => sum + exp.amount, 0),
    [expenses],
  );

  const value = useMemo(
    () => ({ expenses, addExpense, deleteExpense, total }),
    [expenses, total],
  );

  return (
    <ExpenseContext.Provider value={value}>{children}</ExpenseContext.Provider>
  );
};

export const useExpenses = () => {
  const ctx = useContext(ExpenseContext);
  if (!ctx) throw new Error('useExpenses must be used within ExpenseProvider');
  return ctx;
};
